// Small form widgets for the settings-style tabs and the Settings dialog.
import { useState, type ReactNode } from 'react';

export function Sec({ title, children }: { title: string; children: ReactNode }) {
  return <div className="sec"><h4>{title}</h4>{children}</div>;
}

export const Frm = ({ children }: { children: ReactNode }) => <div className="frm">{children}</div>;
export const Hint = ({ children }: { children: ReactNode }) => <span className="hint">{children}</span>;

interface SelProps<T extends string | number> {
  label: string;
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (v: T) => void;
  changed?: boolean;
  disabled?: boolean;
  id?: string;
}

/** Labelled select; `changed` marks a non-default value in the accent colour. */
export function Sel<T extends string | number>({ label, value, options, onChange, changed, disabled, id }: SelProps<T>) {
  const sid = id ?? `sel-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <>
      <label htmlFor={sid}>{label}{changed && <span className="chgmark">changed</span>}</label>
      <select id={sid} className={changed ? 'chg' : ''} value={String(value)} disabled={disabled}
        onChange={(e) => { const o = options.find(([k]) => String(k) === e.target.value); if (o) onChange(o[0]); }}>
        {options.map(([k, l]) => <option key={String(k)} value={String(k)}>{l}</option>)}
      </select>
    </>
  );
}

/** Number input that edits live and reformats on blur. */
export function Num({ value, onChange, label, step, disabled, className, placeholder }: { value: number; onChange: (n: number) => void; label?: string; step?: number; disabled?: boolean; className?: string; placeholder?: string }) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input type="text" inputMode="decimal" aria-label={label} disabled={disabled} className={className} placeholder={placeholder}
      value={text ?? (Number.isFinite(value) ? String(Math.round(value * 100) / 100) : '')}
      onFocus={() => setText(String(Math.round(value * 100) / 100))}
      onChange={(e) => { setText(e.target.value); const n = parseFloat(e.target.value.replace(/[$,%\s]/g, '')); onChange(Number.isFinite(n) ? n : 0); }}
      onBlur={() => setText(null)} step={step} />
  );
}

export function Check({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (b: boolean) => void; disabled?: boolean }) {
  return <label style={{ display: 'block' }}><input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}</label>;
}

export function Two({ labels, children }: { labels: [string, string]; children: [ReactNode, ReactNode] }) {
  return <div className="two"><span className="pa">{labels[0]}</span><span className="pb">{labels[1]}</span>{children[0]}{children[1]}</div>;
}

/** Number input that may be blank: blank gives `null` (or `blankValue` when given). */
export function OptNum({ value, onChange, label, placeholder, className, disabled }: { value: number | null; onChange: (n: number | null) => void; label?: string; placeholder?: string; className?: string; disabled?: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (value === null || !Number.isFinite(value) ? '' : String(Math.round(value * 100) / 100));
  return (
    <input type="text" inputMode="decimal" aria-label={label} placeholder={placeholder} className={className} disabled={disabled} value={shown}
      onFocus={() => setText(shown)}
      onChange={(e) => { setText(e.target.value); const t = e.target.value.replace(/[$,%\s]/g, ''); const n = parseFloat(t); onChange(t === '' || !Number.isFinite(n) ? null : n); }}
      onBlur={() => setText(null)} />
  );
}
